import { useEffect, useState } from 'react';
import {
  createInvoice,
  deepen,
  extraPull,
  fetchProducts,
  fetchTodayPulls,
  spread,
  yesNo,
  type DeepSide,
  type ProductView,
  type SpreadResponse,
  type YesNo,
} from '../api';

// Conversion-optimized upsell shown ALWAYS under the daily card.
// Hero CTA depends on whether the daily was already drawn (deepen vs draw-another).
// Forecast + subscription tiles collapse behind "More options" to let the
// BiteNote ad (above hero) and the primary Stars products lead the panel.

const AD_LABELS: Record<string, { label: string; body: string; cta: string }> = {
  pl: { label: 'Reklama', body: '📷 BiteNote — sfotografuj jedzenie → kalorie. 7 dni Pro gratis.', cta: 'Otwórz' },
  en: { label: 'Ad',      body: '📷 BiteNote — snap food → get calories. 7 days Pro free.',        cta: 'Open' },
  uk: { label: 'Реклама', body: '📷 BiteNote — сфотографуй їжу → калорії. 7 днів Pro безкоштовно.', cta: 'Відкрити' },
  ru: { label: 'Реклама', body: '📷 BiteNote — сфотографируй еду → калории. 7 дней Pro бесплатно.', cta: 'Открыть' },
  hi: { label: 'विज्ञापन', body: '📷 BiteNote — खाने की फोटो → कैलोरी। 7 दिन Pro फ्री।',            cta: 'खोलें' },
  pt: { label: 'Anúncio', body: '📷 BiteNote — foto da comida → calorias. 7 dias Pro grátis.',     cta: 'Abrir' },
  es: { label: 'Anuncio', body: '📷 BiteNote — foto de comida → calorías. 7 días Pro gratis.',     cta: 'Abrir' },
  de: { label: 'Werbung', body: '📷 BiteNote — Essen fotografieren → Kalorien. 7 Tage Pro gratis.',cta: 'Öffnen' },
  fr: { label: 'Publicité', body: '📷 BiteNote — photographie un repas → calories. 7 jours Pro offerts.', cta: 'Ouvrir' },
};

const MORE_LABELS: Record<string, string> = {
  pl: 'Więcej opcji',
  en: 'More options',
  uk: 'Більше варіантів',
  ru: 'Больше вариантов',
  hi: 'और विकल्प',
  pt: 'Mais opções',
  es: 'Más opciones',
  de: 'Mehr Optionen',
  fr: 'Plus d\'options',
};

const BITENOTE_AD_URL = 'https://t.me/bitenotebot?start=tarot_daily';

function AdTile({ lang }: { lang: string }) {
  const a = AD_LABELS[lang] ?? AD_LABELS.en!;
  return (
    <a className="upsell-ad" href={BITENOTE_AD_URL} target="_blank" rel="noopener noreferrer">
      <div className="upsell-ad-label">{a.label}</div>
      <div className="upsell-ad-body">{a.body}</div>
      <div className="upsell-ad-cta">{a.cta} →</div>
    </a>
  );
}

const STR: Record<string, {
  heroDeepen: string;
  heroDeepenHint: string;
  heroExtra: string;
  heroExtraHint: string;
  yesNo: string;
  yesNoDesc: string;
  threeCard: string;
  threeCardDesc: string;
  love: string;
  loveDesc: string;
  career: string;
  careerDesc: string;
  horseshoe: string;
  horseshoeDesc: string;
  celtic: string;
  celticDesc: string;
  month: string;
  monthDesc: string;
  year: string;
  yearDesc: string;
  proSub: string;
  proSubDesc: string;
  premSub: string;
  premSubDesc: string;
  perMonth: string;
  loadingShort: string;
  paymentFailed: string;
  badgePopular: string;
  badgeBest: string;
  badgeNew: string;
  groupQuick: string;
  groupReadings: string;
  groupForecasts: string;
  groupSubs: string;
  deepLove: string;
  deepCareer: string;
  deepFinance: string;
  deepAdvice: string;
  yesLabel: string;
  noLabel: string;
  maybeLabel: string;
  drewLabel: string;
}> = {
  pl: {
    heroDeepen: 'Pogłęb dzisiejszą kartę',
    heroDeepenHint: 'Miłość · praca · finanse · rada — kontekst dla karty dnia.',
    heroExtra: 'Dodatkowa karta',
    heroExtraHint: 'Druga perspektywa na ten sam dzień.',
    yesNo: 'Pytanie tak/nie',
    yesNoDesc: 'Jedna karta z jasną odpowiedzią.',
    threeCard: 'Rozkład 3 karty',
    threeCardDesc: 'Przeszłość · teraźniejszość · przyszłość',
    love: 'Rozkład miłosny',
    loveDesc: 'Ty · druga osoba · wspólna droga',
    career: 'Rozkład zawodowy',
    careerDesc: 'Sytuacja · wyzwanie · wynik',
    horseshoe: 'Podkowa (5 kart)',
    horseshoeDesc: 'Przeszłość · teraz · ukryte · rada · wynik',
    celtic: 'Krzyż celtycki',
    celticDesc: 'Pełny rozkład 10-kartowy',
    month: 'Prognoza miesięczna',
    monthDesc: '4 karty — jedna na każdy tydzień',
    year: 'Koło roku',
    yearDesc: '12 kart — jedna na każdy miesiąc',
    proSub: 'Pro',
    proSubDesc: 'Bez limitów: codzienne, 3-kart, pogłębienia, tak/nie',
    premSub: 'Premium',
    premSubDesc: 'Wszystko Pro + krzyż celtycki + prognoza miesięczna',
    perMonth: '/mies.',
    loadingShort: '…',
    paymentFailed: 'Płatność nie przeszła',
    badgePopular: 'POPULARNE',
    badgeBest: 'NAJLEPSZE',
    badgeNew: 'NOWOŚĆ',
    groupQuick: 'Szybkie wglądy',
    groupReadings: 'Pełne rozkłady',
    groupForecasts: 'Prognozy',
    groupSubs: 'Subskrypcje',
    deepLove: 'Miłość', deepCareer: 'Praca', deepFinance: 'Finanse', deepAdvice: 'Rada',
    yesLabel: 'TAK', noLabel: 'NIE', maybeLabel: 'MOŻLIWE',
    drewLabel: 'Wylosowana karta',
  },
  en: {
    heroDeepen: 'Deepen today\'s card',
    heroDeepenHint: 'Love · career · money · advice — context for your daily.',
    heroExtra: 'Draw another card',
    heroExtraHint: 'A second angle on the same day.',
    yesNo: 'Yes/No question', yesNoDesc: 'One card, clear answer.',
    threeCard: '3-card spread', threeCardDesc: 'Past · present · future',
    love: 'Love spread', loveDesc: 'You · the other · the path between',
    career: 'Career spread', careerDesc: 'Situation · challenge · outcome',
    horseshoe: 'Horseshoe (5 cards)', horseshoeDesc: 'Past · now · hidden · advice · outcome',
    celtic: 'Celtic cross', celticDesc: 'Full 10-card spread',
    month: 'Monthly forecast', monthDesc: '4 cards — one per week',
    year: 'Wheel of the year', yearDesc: '12 cards — one per month',
    proSub: 'Pro', proSubDesc: 'Unlimited: daily, 3-card, deepenings, yes/no',
    premSub: 'Premium', premSubDesc: 'Everything Pro + Celtic cross + monthly forecast',
    perMonth: '/mo', loadingShort: '…', paymentFailed: 'Payment failed',
    badgePopular: 'POPULAR', badgeBest: 'BEST', badgeNew: 'NEW',
    groupQuick: 'Quick insights', groupReadings: 'Full readings', groupForecasts: 'Forecasts', groupSubs: 'Subscriptions',
    deepLove: 'Love', deepCareer: 'Career', deepFinance: 'Money', deepAdvice: 'Advice',
    yesLabel: 'YES', noLabel: 'NO', maybeLabel: 'POSSIBLE',
    drewLabel: 'Drawn card',
  },
  uk: {
    heroDeepen: 'Поглибити сьогоднішню карту', heroDeepenHint: 'Кохання · робота · гроші · порада — контекст для карти дня.',
    heroExtra: 'Витягти ще одну', heroExtraHint: 'Другий ракурс на той самий день.',
    yesNo: 'Питання так/ні', yesNoDesc: 'Одна карта — чітка відповідь.',
    threeCard: 'Розклад на 3 карти', threeCardDesc: 'Минуле · теперішнє · майбутнє',
    love: 'Любовний розклад', loveDesc: 'Ви · інша людина · спільний шлях',
    career: 'Кар\'єрний розклад', careerDesc: 'Ситуація · виклик · результат',
    horseshoe: 'Підкова (5 карт)', horseshoeDesc: 'Минуле · зараз · приховане · порада · результат',
    celtic: 'Кельтський хрест', celticDesc: 'Повний розклад на 10 карт',
    month: 'Місячний прогноз', monthDesc: '4 карти — по одній на тиждень',
    year: 'Колесо року', yearDesc: '12 карт — по одній на місяць',
    proSub: 'Pro', proSubDesc: 'Без лімітів: щоденне, 3-карти, поглиблення, так/ні',
    premSub: 'Premium', premSubDesc: 'Все Pro + кельтський хрест + місячний прогноз',
    perMonth: '/міс.', loadingShort: '…', paymentFailed: 'Помилка платежу',
    badgePopular: 'ПОПУЛЯРНЕ', badgeBest: 'НАЙКРАЩЕ', badgeNew: 'НОВЕ',
    groupQuick: 'Швидкі вгляди', groupReadings: 'Повні розклади', groupForecasts: 'Прогнози', groupSubs: 'Підписки',
    deepLove: 'Кохання', deepCareer: 'Робота', deepFinance: 'Гроші', deepAdvice: 'Порада',
    yesLabel: 'ТАК', noLabel: 'НІ', maybeLabel: 'МОЖЛИВО',
    drewLabel: 'Витягнута карта',
  },
  ru: {
    heroDeepen: 'Углубить сегодняшнюю карту', heroDeepenHint: 'Любовь · работа · деньги · совет — контекст для карты дня.',
    heroExtra: 'Вытянуть ещё одну', heroExtraHint: 'Второй ракурс на тот же день.',
    yesNo: 'Вопрос да/нет', yesNoDesc: 'Одна карта — чёткий ответ.',
    threeCard: 'Расклад на 3 карты', threeCardDesc: 'Прошлое · настоящее · будущее',
    love: 'Любовный расклад', loveDesc: 'Вы · другой · общий путь',
    career: 'Карьерный расклад', careerDesc: 'Ситуация · вызов · результат',
    horseshoe: 'Подкова (5 карт)', horseshoeDesc: 'Прошлое · сейчас · скрытое · совет · итог',
    celtic: 'Кельтский крест', celticDesc: 'Полный расклад из 10 карт',
    month: 'Месячный прогноз', monthDesc: '4 карты — по одной на неделю',
    year: 'Колесо года', yearDesc: '12 карт — по одной на месяц',
    proSub: 'Pro', proSubDesc: 'Без лимитов: ежедневное, 3-карты, углубления, да/нет',
    premSub: 'Premium', premSubDesc: 'Всё Pro + кельтский крест + месячный прогноз',
    perMonth: '/мес.', loadingShort: '…', paymentFailed: 'Ошибка платежа',
    badgePopular: 'ПОПУЛЯРНОЕ', badgeBest: 'ЛУЧШЕЕ', badgeNew: 'НОВОЕ',
    groupQuick: 'Быстрые инсайты', groupReadings: 'Полные расклады', groupForecasts: 'Прогнозы', groupSubs: 'Подписки',
    deepLove: 'Любовь', deepCareer: 'Работа', deepFinance: 'Деньги', deepAdvice: 'Совет',
    yesLabel: 'ДА', noLabel: 'НЕТ', maybeLabel: 'ВОЗМОЖНО',
    drewLabel: 'Вытянутая карта',
  },
  hi: {
    heroDeepen: 'आज का कार्ड गहराई से', heroDeepenHint: 'प्रेम · करियर · पैसा · सलाह — आज के कार्ड के संदर्भ में।',
    heroExtra: 'एक और कार्ड खींचें', heroExtraHint: 'उसी दिन का दूसरा पहलू।',
    yesNo: 'हाँ/ना प्रश्न', yesNoDesc: 'एक कार्ड — स्पष्ट उत्तर।',
    threeCard: '3-कार्ड स्प्रेड', threeCardDesc: 'अतीत · वर्तमान · भविष्य',
    love: 'प्रेम स्प्रेड', loveDesc: 'आप · वह · साझा मार्ग',
    career: 'करियर स्प्रेड', careerDesc: 'स्थिति · चुनौती · परिणाम',
    horseshoe: 'हॉर्सशू (5 कार्ड)', horseshoeDesc: 'अतीत · अभी · छिपा · सलाह · परिणाम',
    celtic: 'सेल्टिक क्रॉस', celticDesc: 'पूर्ण 10-कार्ड स्प्रेड',
    month: 'मासिक पूर्वानुमान', monthDesc: '4 कार्ड — हर हफ़्ते एक',
    year: 'वर्ष का चक्र', yearDesc: '12 कार्ड — हर महीने एक',
    proSub: 'Pro', proSubDesc: 'असीमित: दैनिक, 3-कार्ड, गहराई, हाँ/ना',
    premSub: 'Premium', premSubDesc: 'सब कुछ Pro + सेल्टिक क्रॉस + मासिक पूर्वानुमान',
    perMonth: '/माह', loadingShort: '…', paymentFailed: 'भुगतान विफल',
    badgePopular: 'लोकप्रिय', badgeBest: 'बेस्ट', badgeNew: 'नया',
    groupQuick: 'त्वरित अंतर्दृष्टि', groupReadings: 'पूर्ण रीडिंग', groupForecasts: 'पूर्वानुमान', groupSubs: 'सदस्यताएँ',
    deepLove: 'प्रेम', deepCareer: 'करियर', deepFinance: 'पैसा', deepAdvice: 'सलाह',
    yesLabel: 'हाँ', noLabel: 'नहीं', maybeLabel: 'शायद',
    drewLabel: 'खींचा गया कार्ड',
  },
  pt: {
    heroDeepen: 'Aprofundar a carta de hoje', heroDeepenHint: 'Amor · trabalho · dinheiro · conselho — contexto para a carta do dia.',
    heroExtra: 'Tirar outra carta', heroExtraHint: 'Um segundo ângulo do mesmo dia.',
    yesNo: 'Pergunta sim/não', yesNoDesc: 'Uma carta — resposta clara.',
    threeCard: 'Jogo de 3 cartas', threeCardDesc: 'Passado · presente · futuro',
    love: 'Jogo do amor', loveDesc: 'Você · a outra pessoa · caminho comum',
    career: 'Jogo do trabalho', careerDesc: 'Situação · desafio · resultado',
    horseshoe: 'Ferradura (5 cartas)', horseshoeDesc: 'Passado · agora · oculto · conselho · resultado',
    celtic: 'Cruz celta', celticDesc: 'Jogo completo de 10 cartas',
    month: 'Previsão mensal', monthDesc: '4 cartas — uma por semana',
    year: 'Roda do ano', yearDesc: '12 cartas — uma por mês',
    proSub: 'Pro', proSubDesc: 'Sem limites: diária, 3-cartas, aprofundamentos, sim/não',
    premSub: 'Premium', premSubDesc: 'Tudo Pro + cruz celta + previsão mensal',
    perMonth: '/mês', loadingShort: '…', paymentFailed: 'Pagamento falhou',
    badgePopular: 'POPULAR', badgeBest: 'MELHOR', badgeNew: 'NOVO',
    groupQuick: 'Insights rápidos', groupReadings: 'Leituras completas', groupForecasts: 'Previsões', groupSubs: 'Assinaturas',
    deepLove: 'Amor', deepCareer: 'Trabalho', deepFinance: 'Dinheiro', deepAdvice: 'Conselho',
    yesLabel: 'SIM', noLabel: 'NÃO', maybeLabel: 'POSSÍVEL',
    drewLabel: 'Carta tirada',
  },
  es: {
    heroDeepen: 'Profundizar la carta de hoy', heroDeepenHint: 'Amor · trabajo · dinero · consejo — contexto para tu carta del día.',
    heroExtra: 'Sacar otra carta', heroExtraHint: 'Un segundo ángulo del mismo día.',
    yesNo: 'Pregunta sí/no', yesNoDesc: 'Una carta — respuesta clara.',
    threeCard: 'Tirada de 3 cartas', threeCardDesc: 'Pasado · presente · futuro',
    love: 'Tirada del amor', loveDesc: 'Tú · la otra persona · camino común',
    career: 'Tirada del trabajo', careerDesc: 'Situación · desafío · resultado',
    horseshoe: 'Herradura (5 cartas)', horseshoeDesc: 'Pasado · ahora · oculto · consejo · resultado',
    celtic: 'Cruz celta', celticDesc: 'Tirada completa de 10 cartas',
    month: 'Pronóstico mensual', monthDesc: '4 cartas — una por semana',
    year: 'Rueda del año', yearDesc: '12 cartas — una por mes',
    proSub: 'Pro', proSubDesc: 'Sin límites: diaria, 3-cartas, profundizaciones, sí/no',
    premSub: 'Premium', premSubDesc: 'Todo Pro + cruz celta + pronóstico mensual',
    perMonth: '/mes', loadingShort: '…', paymentFailed: 'Pago fallido',
    badgePopular: 'POPULAR', badgeBest: 'MEJOR', badgeNew: 'NUEVO',
    groupQuick: 'Insights rápidos', groupReadings: 'Lecturas completas', groupForecasts: 'Pronósticos', groupSubs: 'Suscripciones',
    deepLove: 'Amor', deepCareer: 'Trabajo', deepFinance: 'Dinero', deepAdvice: 'Consejo',
    yesLabel: 'SÍ', noLabel: 'NO', maybeLabel: 'POSIBLE',
    drewLabel: 'Carta sacada',
  },
  de: {
    heroDeepen: 'Heutige Karte vertiefen', heroDeepenHint: 'Liebe · Beruf · Geld · Rat — Kontext zur Tageskarte.',
    heroExtra: 'Noch eine Karte ziehen', heroExtraHint: 'Ein zweiter Blickwinkel auf den gleichen Tag.',
    yesNo: 'Ja/Nein-Frage', yesNoDesc: 'Eine Karte — klare Antwort.',
    threeCard: '3-Karten-Legung', threeCardDesc: 'Vergangenheit · Gegenwart · Zukunft',
    love: 'Liebeslegung', loveDesc: 'Du · die andere Person · gemeinsamer Weg',
    career: 'Karrierelegung', careerDesc: 'Situation · Herausforderung · Ergebnis',
    horseshoe: 'Hufeisen (5 Karten)', horseshoeDesc: 'Vergangenheit · jetzt · Verborgenes · Rat · Ergebnis',
    celtic: 'Keltisches Kreuz', celticDesc: 'Vollständige 10-Karten-Legung',
    month: 'Monatsprognose', monthDesc: '4 Karten — eine pro Woche',
    year: 'Jahresrad', yearDesc: '12 Karten — eine pro Monat',
    proSub: 'Pro', proSubDesc: 'Unbegrenzt: täglich, 3-Karten, Vertiefungen, Ja/Nein',
    premSub: 'Premium', premSubDesc: 'Alles Pro + keltisches Kreuz + Monatsprognose',
    perMonth: '/Mon.', loadingShort: '…', paymentFailed: 'Zahlung fehlgeschlagen',
    badgePopular: 'BELIEBT', badgeBest: 'BESTE', badgeNew: 'NEU',
    groupQuick: 'Schnelle Einblicke', groupReadings: 'Vollständige Legungen', groupForecasts: 'Prognosen', groupSubs: 'Abos',
    deepLove: 'Liebe', deepCareer: 'Beruf', deepFinance: 'Geld', deepAdvice: 'Rat',
    yesLabel: 'JA', noLabel: 'NEIN', maybeLabel: 'MÖGLICH',
    drewLabel: 'Gezogene Karte',
  },
  fr: {
    heroDeepen: 'Approfondir la carte du jour', heroDeepenHint: 'Amour · travail · argent · conseil — contexte pour la carte du jour.',
    heroExtra: 'Tirer une autre carte', heroExtraHint: 'Un second angle sur la même journée.',
    yesNo: 'Question oui/non', yesNoDesc: 'Une carte — réponse claire.',
    threeCard: 'Tirage 3 cartes', threeCardDesc: 'Passé · présent · futur',
    love: 'Tirage d\'amour', loveDesc: 'Vous · l\'autre · le chemin commun',
    career: 'Tirage carrière', careerDesc: 'Situation · défi · résultat',
    horseshoe: 'Fer à cheval (5 cartes)', horseshoeDesc: 'Passé · maintenant · caché · conseil · résultat',
    celtic: 'Croix celtique', celticDesc: 'Tirage complet de 10 cartes',
    month: 'Prévision mensuelle', monthDesc: '4 cartes — une par semaine',
    year: 'Roue de l\'année', yearDesc: '12 cartes — une par mois',
    proSub: 'Pro', proSubDesc: 'Sans limite : quotidien, 3-cartes, approfondissements, oui/non',
    premSub: 'Premium', premSubDesc: 'Tout Pro + croix celtique + prévision mensuelle',
    perMonth: '/mois', loadingShort: '…', paymentFailed: 'Paiement échoué',
    badgePopular: 'POPULAIRE', badgeBest: 'MEILLEUR', badgeNew: 'NOUVEAU',
    groupQuick: 'Aperçus rapides', groupReadings: 'Lectures complètes', groupForecasts: 'Prévisions', groupSubs: 'Abonnements',
    deepLove: 'Amour', deepCareer: 'Travail', deepFinance: 'Argent', deepAdvice: 'Conseil',
    yesLabel: 'OUI', noLabel: 'NON', maybeLabel: 'POSSIBLE',
    drewLabel: 'Carte tirée',
  },
};

interface Props {
  lang: string;
  alreadyPulled: boolean;
  onPaid?: () => void;
}

interface ResultBox {
  kind: 'yesno' | 'deepen' | 'spread' | 'extra';
  data: unknown;
}

export function UpsellPanel({ lang, alreadyPulled, onPaid }: Props) {
  const [products, setProducts] = useState<ProductView[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Array (not single slot) so buying a second extra card does not overwrite
  // the first. Seeded from /api/today-pulls so results survive reload.
  const [results, setResults] = useState<ResultBox[]>([]);
  // adFree: any payment in last 30 days OR active sub. Hides the BiteNote ad.
  // Default true (no flash of ad while loading) — flips to true/false after first fetch.
  const [adFree, setAdFree] = useState<boolean>(true);
  const s = STR[lang] ?? STR.en!;

  const appendResult = (r: ResultBox) => setResults((prev) => [...prev, r]);
  const dismissResult = (idx: number) =>
    setResults((prev) => prev.filter((_, i) => i !== idx));

  const refreshAdState = () => {
    fetchTodayPulls()
      .then((r) => {
        setResults(r.results as ResultBox[]);
        setAdFree(!!r.adFree);
      })
      .catch(() => {});
  };

  useEffect(() => {
    fetchProducts().then((r) => setProducts(r.products)).catch(() => {});
    refreshAdState();
  }, []);

  const priceOf = (id: string) => products?.find((p) => p.id === id)?.priceStars ?? 0;

  async function openInvoice(productId: string, onSuccess?: () => Promise<void> | void) {
    setBusy(productId);
    setErr(null);
    try {
      const { invoiceLink } = await createInvoice(productId);
      // @ts-expect-error — Telegram WebApp global
      const tg = window.Telegram?.WebApp;
      if (!tg?.openInvoice) throw new Error('not in Telegram');
      await new Promise<void>((resolve, reject) => {
        tg.openInvoice(invoiceLink, async (status: 'paid' | 'cancelled' | 'failed') => {
          if (status === 'paid') {
            try {
              await onSuccess?.();
              onPaid?.();
              // Any payment grants 30-day ad-free — refresh the flag so the
              // BiteNote tile disappears without reload.
              refreshAdState();
              resolve();
            }
            catch (e) { reject(e as Error); }
          } else if (status === 'cancelled') resolve();
          else reject(new Error(s.paymentFailed));
        });
      });
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  const fireDeepen = () => openInvoice('deepen', async () => {
    const r = await deepen();
    appendResult({ kind: 'deepen', data: r });
  });
  const fireExtra = () => openInvoice('extra-pull', async () => {
    const r = await extraPull();
    appendResult({ kind: 'extra', data: r });
  });
  const fireYesNo = () => openInvoice('yes-no', async () => {
    const r = await yesNo();
    appendResult({ kind: 'yesno', data: r });
  });
  const fireSpread = (productId: string, type: 'love' | 'career' | '3-card' | 'horseshoe' | 'celtic' | 'month' | 'year') =>
    openInvoice(productId, async () => {
      const r = await spread(type);
      appendResult({ kind: 'spread', data: r });
    });

  const heroAction = alreadyPulled ? fireDeepen : fireDeepen; // deepen is the natural next step in both states
  const heroLabel = s.heroDeepen;
  const heroHint = s.heroDeepenHint;
  const heroPriceProductId = 'deepen';
  const heroBusy = busy === heroPriceProductId;

  const Tile = ({ id, type, title, desc, onClick, badge, secondaryPriceLabel }: {
    id: string;
    type: string;
    title: string;
    desc: string;
    onClick: () => void;
    badge?: string;
    secondaryPriceLabel?: string;
  }) => (
    <button className={`upsell-tile upsell-tile-${type}`} disabled={busy === id} onClick={onClick}>
      {badge && <div className="upsell-tile-badge">{badge}</div>}
      <div className="upsell-tile-title">{title}</div>
      <div className="upsell-tile-desc">{desc}</div>
      <div className="upsell-tile-price">
        {priceOf(id)} ⭐{secondaryPriceLabel && <span className="upsell-tile-period">{secondaryPriceLabel}</span>}
      </div>
    </button>
  );

  const moreLabel = MORE_LABELS[lang] ?? MORE_LABELS.en!;

  return (
    <section className="upsell">
      {!adFree && <AdTile lang={lang} />}
      <div className="upsell-hero" role="button" tabIndex={0} onClick={heroAction}>
        <div className="upsell-hero-text">
          <h2 className="upsell-hero-title">{heroLabel}</h2>
          <p className="upsell-hero-hint">{heroHint}</p>
        </div>
        <button className="upsell-hero-btn" disabled={heroBusy}>
          <span className="upsell-hero-btn-label">{heroBusy ? s.loadingShort : heroLabel}</span>
          {priceOf(heroPriceProductId) > 0 && (
            <span className="upsell-hero-btn-price">{priceOf(heroPriceProductId)} ⭐</span>
          )}
        </button>
      </div>

      {results.length > 0 && (
        <div className="upsell-results">
          {results.map((r, i) => (
            <ResultView
              key={i}
              lang={lang}
              s={s}
              result={r}
              onClose={() => dismissResult(i)}
            />
          ))}
        </div>
      )}

      <div className="upsell-group-label">{s.groupQuick}</div>
      <div className="upsell-grid">
        <Tile id="extra-pull" type="quick" title={s.heroExtra} desc={s.heroExtraHint} onClick={fireExtra} />
        <Tile id="yes-no" type="quick" title={s.yesNo} desc={s.yesNoDesc} onClick={fireYesNo} badge={s.badgeNew} />
      </div>

      <div className="upsell-group-label">{s.groupReadings}</div>
      <div className="upsell-grid">
        <Tile id="3-card" type="reading" title={s.threeCard} desc={s.threeCardDesc} onClick={() => fireSpread('3-card', '3-card')} badge={s.badgePopular} />
        <Tile id="love-spread" type="reading" title={s.love} desc={s.loveDesc} onClick={() => fireSpread('love-spread', 'love')} badge={s.badgeNew} />
        <Tile id="career-spread" type="reading" title={s.career} desc={s.careerDesc} onClick={() => fireSpread('career-spread', 'career')} badge={s.badgeNew} />
        <Tile id="horseshoe" type="reading" title={s.horseshoe} desc={s.horseshoeDesc} onClick={() => fireSpread('horseshoe', 'horseshoe')} />
        <Tile id="celtic" type="reading" title={s.celtic} desc={s.celticDesc} onClick={() => fireSpread('celtic', 'celtic')} />
      </div>

      <details className="upsell-more">
        <summary className="upsell-more-summary">{moreLabel}</summary>

        <div className="upsell-group-label">{s.groupForecasts}</div>
        <div className="upsell-grid">
          <Tile id="month-forecast" type="forecast" title={s.month} desc={s.monthDesc} onClick={() => fireSpread('month-forecast', 'month')} />
          <Tile id="year-wheel" type="forecast" title={s.year} desc={s.yearDesc} onClick={() => fireSpread('year-wheel', 'year')} badge={s.badgeBest} />
        </div>

        <div className="upsell-group-label">{s.groupSubs}</div>
        <div className="upsell-grid">
          <Tile id="pro-sub-monthly" type="sub" title={s.proSub} desc={s.proSubDesc} onClick={() => openInvoice('pro-sub-monthly')} badge={s.badgePopular} secondaryPriceLabel={s.perMonth} />
          <Tile id="premium-sub-monthly" type="sub-premium" title={s.premSub} desc={s.premSubDesc} onClick={() => openInvoice('premium-sub-monthly')} badge={s.badgeBest} secondaryPriceLabel={s.perMonth} />
        </div>
      </details>

      {err && <div className="upsell-error">{err}</div>}
    </section>
  );
}

// ── Result rendering ──────────────────────────────────────────────────────
function ResultView({ lang, s, result, onClose }: {
  lang: string;
  s: typeof STR[keyof typeof STR];
  result: ResultBox;
  onClose: () => void;
}) {
  if (result.kind === 'deepen') {
    const r = result.data as { card: { name: string; image: string }; reversed: boolean; deep?: DeepSide };
    const d = r.deep;
    if (!d) return null;
    return (
      <div className="upsell-result">
        <div className="upsell-result-head">{r.card.name}</div>
        <div className="upsell-result-grid">
          <Section label={s.deepLove} body={d.love} />
          <Section label={s.deepCareer} body={d.career} />
          <Section label={s.deepFinance} body={d.finance} />
          <Section label={s.deepAdvice} body={d.advice} />
        </div>
        <button className="upsell-result-close" onClick={onClose}>×</button>
      </div>
    );
  }
  if (result.kind === 'yesno') {
    const r = result.data as { card: { name: string; image: string }; answer: YesNo; reversed: boolean };
    const label = r.answer === 'yes' ? s.yesLabel : r.answer === 'no' ? s.noLabel : s.maybeLabel;
    return (
      <div className="upsell-result">
        <div className={`upsell-yesno upsell-yesno-${r.answer}`}>{label}</div>
        <div className="upsell-result-head">{r.card.name}</div>
        <button className="upsell-result-close" onClick={onClose}>×</button>
      </div>
    );
  }
  if (result.kind === 'extra') {
    const r = result.data as { card: { name: string; image: string }; reversed: boolean; meaning: { upright: string; reversed: string } };
    const text = r.reversed ? r.meaning.reversed : r.meaning.upright;
    return (
      <div className="upsell-result">
        <div className="upsell-result-head">{s.drewLabel}: {r.card.name}</div>
        <div className="upsell-result-body">{text}</div>
        <button className="upsell-result-close" onClick={onClose}>×</button>
      </div>
    );
  }
  if (result.kind === 'spread') {
    const r = result.data as SpreadResponse;
    return (
      <div className="upsell-result">
        <div className="upsell-result-head">{r.type}</div>
        <div className="upsell-spread-list">
          {r.cards.map((c, i) => (
            <div key={i} className="upsell-spread-item">
              <div className="upsell-spread-pos">{c.position ?? `#${i + 1}`}</div>
              <div className="upsell-spread-name">{c.card.name}</div>
              <div className="upsell-spread-text">{c.reversed ? c.meaning.reversed : c.meaning.upright}</div>
            </div>
          ))}
        </div>
        <button className="upsell-result-close" onClick={onClose}>×</button>
      </div>
    );
  }
  return null;
}

function Section({ label, body }: { label: string; body: string }) {
  return (
    <div className="upsell-section">
      <div className="upsell-section-label">{label}</div>
      <div className="upsell-section-body">{body}</div>
    </div>
  );
}
